import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["bar", "currentStep", "nextButton", "backButton", "submitButton", "form", "step"];
  static values = { scrollToTop: Boolean };

  connect() {
    this.element.addEventListener("progress:nextStep", this.nextStep.bind(this));
    this.element.addEventListener("progress:backStep", this.backStep.bind(this));
  }

  async nextStep() {
    const currentStep = parseInt(this.element.dataset.currentStep, 10);
    const oldBar = this.barTargets[currentStep - 1];
    const newBar = this.barTargets[currentStep];

    if (oldBar) {
      await this.fillBar(oldBar, 100);
    }

    if (newBar) {
      await this.fillBar(newBar, 10);
      this.element.dataset.currentStep = currentStep + 1;
    }

    this.toggleStepVisibility(currentStep + 1);

    this.updateButtonVisibility(currentStep + 1);
    
    this.scrollToTop();
  }

  async backStep() {
    const currentStep = parseInt(this.element.dataset.currentStep, 10);
    const oldBar = this.barTargets[currentStep - 1];
    const newBar = this.barTargets[currentStep - 2];

    if (oldBar) {
      await this.fillBar(oldBar, 0);
    }

    if (newBar) {
      await this.fillBar(newBar, 10);
    }

    this.toggleStepVisibility(currentStep - 1);

    this.updateButtonVisibility(currentStep - 1);
    
    this.element.dataset.currentStep = currentStep - 1;
    
    this.scrollToTop();
  }

  onNext(event) {
    event.preventDefault();
    const currentStep = parseInt(this.element.dataset.currentStep, 10);
    const currentStepElement = this.stepTargets[currentStep - 1];
    if (!this.validateFieldsIn(currentStepElement)) {
      return;
    }
    this.nextStep();
    this.submitOnNext();
    if (!this.hasFormTarget && event.target.closest("button[type='submit']") && event.target.closest("form")) {
      this.submitAction(event.target.closest("form"), {});
    }
  }

  onBack(event) {
    event.preventDefault();
    this.backStep();
  }

  submitOnNext() {
    if (this.hasFormTarget && this.element.dataset.submitOnNext === "true") {
      this.submitAction(this.formTarget, {review: true});
    }
  }

  submitAction(form, params) {
    const formData = new FormData(form);
    if (params) {
      Object.keys(params).forEach(key => formData.append(key, params[key]));
    }
    const formAction = form.getAttribute("action");
    fetch(formAction, {
      method: "POST",
      body: formData,
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Accept": "text/vnd.turbo-stream.html"
      },
    })
    .then(async response => {
      const turboStream = await response.text();
      if (window.Turbo && window.Turbo.renderStreamMessage) {
        window.Turbo.renderStreamMessage(turboStream);
      } else {
        // fallback: replace body content
        document.body.innerHTML = turboStream;
      }

      setTimeout(() => { this.scrollToTop() }, 300);
    });
  }

  validateFieldsIn(stepElement) {
    const fields = stepElement.querySelectorAll("input, select, textarea");
    let valid = true;

    fields.forEach((field) => {
      if (!field.checkValidity()) {
        field.reportValidity();
        valid = false;
      }
    });
    return valid;
  }

  fillBar(barElement, value) {
    return new Promise((resolve) => {
      if (!barElement) {
        return resolve();
      }

      barElement.setAttribute("aria-valuenow", value);

      const fillElement = barElement.children[0];
      fillElement.style.width = `${value}%`;

      const afterTransition = () => {
        resolve();
      };

      fillElement.addEventListener("transitionend", afterTransition);
    });
  }

  updateButtonVisibility(currentStep) {
    if (this.hasCurrentStepTarget) {
      this.currentStepTarget.textContent = currentStep;
    }

    // Back button visibility
    if (this.hasBackButtonTarget) {
      if (currentStep <= 1) {
        this.backButtonTarget.classList.add("hidden");
      } else {
        this.backButtonTarget.classList.remove("hidden");
      }
    }

    // Next and Submit button visibility
    if (this.hasNextButtonTarget) {
      if (currentStep >= this.barTargets.length) {
        this.nextButtonTarget.classList.add("hidden");
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.classList.remove("hidden");
        }
      } else {
        this.nextButtonTarget.classList.remove("hidden");
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.classList.add("hidden");
        }
      }
    }
  }

  toggleStepVisibility(currentStep) {
    if (this.hasStepTarget) {
      this.stepTargets.forEach((step, index) => {
        step.classList.toggle("hidden", index !== currentStep - 1);
      });
    }
  }

  scrollToTop() {
    if (!this.hasScrollToTopValue || !this.scrollToTopValue) return;

    if (!document.body.contains(this.element)) return;

    const currentStep = parseInt(this.element.dataset.currentStep, 10);
    const currentStepElement = this.stepTargets[currentStep - 1];
    
    if (!currentStepElement) return;

    currentStepElement.scrollIntoView({ behavior: "smooth", block: "start" });
  }

}
